import { useMemo } from 'react'

/**
 * Props:
 * - pageMetadata: { currentPage: number, limitValue: number, totalCount?: number }
 * - onPageChange: (page: number) => void
 * - pageRange: number of pages in the window (default: 5)
 * - classNames (optional): override Bootstrap classes
 * - labels (optional): override button texts
 */
function Pagination({
  pageMetadata = { currentPage: 1, limitValue: 10, totalCount: 0 },
  onPageChange,
  pageRange = 5,
  classNames = {
    ul: 'pagination mt-5',
    li: 'page-item',
    a: 'page-link'
  },
  labels = {
    first: '&laquo;', // «
    last: '&raquo;', // »
    prev: '&lsaquo; Prev', // ‹ Prev
    next: 'Next &rsaquo;' // Next ›
  }
}) {
  const DISABLED = 'disabled'
  const { currentPage = 1, limitValue = 10, totalCount = 30 } = pageMetadata
  const totalPages = Math.max(1, Math.ceil((Number(totalCount) || 0) / Math.max(1, Number(limitValue) || 1)))

  // Normalize the current page within allowed boundaries
  const page = Math.min(Math.max(1, Number(currentPage) || 1), totalPages)

  const pages = useMemo(() => {
    const range = Math.max(1, Number(pageRange) || 1)
    if (totalPages <= range + 2) {
      // few pages – show all
      return Array.from({ length: totalPages }, (_, index) => index + 1)
    }

    const half = Math.floor(range / 2)
    let start = Math.max(1, page - half)
    let end = start + range - 1

    if (end > totalPages) {
      end = totalPages
      start = Math.max(1, end - range + 1)
    }

    const pageOptions = []
    // Always show the first page
    if (start > 1) {
      pageOptions.push(1)
      if (start > 2) pageOptions.push('ellipsis-left')
    }

    for (let pageNumber = start; pageNumber <= end; pageNumber += 1) pageOptions.push(pageNumber)

    // Always show the last page
    if (end < totalPages) {
      if (end < totalPages - 1) pageOptions.push('ellipsis-right')
      pageOptions.push(totalPages)
    }

    return pageOptions
  }, [page, pageRange, totalPages])

  const handleChange = (next) => {
    if (next !== page && next >= 1 && next <= totalPages) onPageChange?.(next)
  }

  const { ul: ulClass, li: liClass, a: aClass } = classNames
  const { first, prev, next, last } = labels

  return (
    <nav aria-label='Pagination'>
      <ul className={ulClass}>
        {/* First */}
        <li className={`${liClass} ${page === 1 ? DISABLED : ''}`}>
          <button
            type='button'
            className={aClass}
            aria-label='First page'
            disabled={page === 1}
            onClick={() => handleChange(1)}
          >
            <span dangerouslySetInnerHTML={{ __html: first }} />
          </button>
        </li>

        {/* Prev */}
        <li className={`${liClass} ${page === 1 ? DISABLED : ''}`}>
          <button
            type='button'
            className={aClass}
            aria-label='Previous page'
            disabled={page === 1}
            onClick={() => handleChange(page - 1)}
          >
            <span dangerouslySetInnerHTML={{ __html: prev }} />
          </button>
        </li>

        {/* Numbered pages */}
        {pages.map((pageNumber) => {
          if (typeof pageNumber === 'string' && pageNumber.startsWith('ellipsis')) {
            return (
              <li key={`${pageNumber}`} className={`${liClass} ${DISABLED}`} aria-hidden='true'>
                <span className={aClass}>…</span>
              </li>
            )
          }
          const isActive = pageNumber === page
          return (
            <li key={pageNumber} className={`${liClass} ${isActive ? 'active' : ''}`} aria-current={isActive ? 'page' : undefined}>
              <button
                type='button'
                className={aClass}
                onClick={() => handleChange(pageNumber)}
                aria-label={`Go to page ${pageNumber}`}
              >
                {pageNumber}
              </button>
            </li>
          )
        })}

        {/* Next */}
        <li className={`${liClass} ${page === totalPages ? DISABLED : ''}`}>
          <button
            type='button'
            className={aClass}
            aria-label='Next page'
            disabled={page === totalPages}
            onClick={() => handleChange(page + 1)}
          >
            <span dangerouslySetInnerHTML={{ __html: next }} />
          </button>
        </li>

        {/* Last */}
        <li className={`${liClass} ${page === totalPages ? DISABLED : ''}`}>
          <button
            type='button'
            className={aClass}
            aria-label='Last page'
            disabled={page === totalPages}
            onClick={() => handleChange(totalPages)}
          >
            <span dangerouslySetInnerHTML={{ __html: last }} />
          </button>
        </li>
      </ul>
    </nav>
  )
}

export default Pagination
